import { genericErrors } from "./errorUtils.mjs"
import { toDashed, toPlural, toSingular } from "./miscUtils.mjs"
import { capitalizeFirstLetter, lowerCaseFirstLetter } from "./stringUtils.mjs"

const coreTenants = {
  MASTER: "master",
  SUPPORT: "support",
  TEST: "test",
  E_INVOICING: "e-invoicing",
}

const pseudoEntities = ["login", "totp", "cron", "import", "mail", "database", "dbdump"]

const entityNames = {
  AccessLog: "AccessLog",
  AccessStat: "AccessStat",
  AccountingCatalogItem: "AccountingCatalogItem",
  Agreement: "Agreement",
  AiAgent: "AiAgent",
  AiConversation: "AiConversation",
  AiMemory: "AiMemory",
  AiConversationMessage: "AiConversationMessage",
  Amendment: "Amendment",
  AmendmentCatalogItem: "AmendmentCatalogItem",
  Asset: "Asset",
  AssetCatalogItem: "AssetCatalogItem",
  AssetServiceCatalogItem: "AssetServiceCatalogItem",
  AssetExpense: "AssetExpense",
  AssetLot: "AssetLot",
  Audit: "Audit",
  Batch: "Batch",
  BankAccount: "BankAccount",
  BankCard: "BankCard",
  BankOperation: "BankOperation",
  BankStatement: "BankStatement",
  BankTransaction: "BankTransaction",
  Booking: "Booking",
  Budget: "Budget",
  Campaign: "Campaign",
  CampaignContact: "CampaignContact",
  Camt: "Camt",
  CamtEntry: "CamtEntry",
  Cashflow: "Cashflow",
  CashflowAllocation: "CashflowAllocation",
  Case: "Case",
  Claim: "Claim",
  Collateral: "Collateral",
  Committee: "Committee",
  Communication: "Communication",
  Contract: "Contract",
  ContractElement: "ContractElement",
  ContractLot: "ContractLot",
  Counter: "Counter",
  Covenant: "Covenant",
  CreditLine: "CreditLine",
  Currency: "Currency",
  Deal: "Deal",
  DepreciationSchedule: "DepreciationSchedule",
  Document: "Document",
  Dtxn: "Dtxn",
  EInvoiceLot: "EInvoiceLot",
  Evaluation: "Evaluation",
  EvaluationCatalogItem: "EvaluationCatalogItem",
  ExternalData: "ExternalData",
  FeatureFlag: "FeatureFlag",
  FeatureFlagItem: "FeatureFlagItem",
  FinancingService: "FinancingService",
  Fine: "Fine",
  Flow: "Flow",
  Fund: "Fund",
  Funding: "Funding",
  History: "History",
  Import: "Import",
  Index: "Index",
  Indice: "Indice",
  Inspection: "Inspection",
  Inventory: "Inventory",
  Invoice: "Invoice",
  InvoiceLine: "InvoiceLine",
  KeyValue: "KeyValue",
  LedgerAccount: "LedgerAccount",
  LedgerPeriod: "LedgerPeriod",
  Lesson: "Lesson",
  LessonCatalogItem: "LessonCatalogItem",
  List: "List",
  Locale: "Locale",
  Location: "Location",
  Maintenance: "Maintenance",
  Metric: "Metric",
  MetricRecord: "MetricRecord",
  Note: "Note",
  PaymentFile: "PaymentFile",
  PaymentFileLine: "PaymentFileLine",
  Person: "Person",
  PersonAmendment: "PersonAmendment",
  Posting: "Posting",
  PostingEvent: "PostingEvent",
  PrivateEquityAsset: "PrivateEquityAsset",
  PrivateEquityOperation: "PrivateEquityOperation",
  PrivateEquityTransaction: "PrivateEquityTransaction",
  Product: "Product",
  ProductCatalogItem: "ProductCatalogItem",
  ProductElementCatalogItem: "ProductElementCatalogItem",
  ProductTable: "ProductTable",
  Project: "Project",
  ProjectEstimate: "ProjectEstimate",
  Prospect: "Prospect",
  Publication: "Publication",
  Question: "Question",
  Report: "Report",
  Rule: "Rule",
  Scheme: "Scheme",
  Score: "Score",
  ScoreCatalogItem: "ScoreCatalogItem",
  Script: "Script",
  ScriptLog: "ScriptLog",
  Sheet: "Sheet",
  Sepa: "Sepa",
  SystemEvent: "SystemEvent",
  SystemLog: "SystemLog",
  Task: "Task",
  Tax: "Tax",
  TmpCollection: "TmpCollection",
  Tenant: "Tenant",
  Ticket: "Ticket",
  TicketLot: "TicketLot",
  TimeEntry: "TimeEntry",
  User: "User",
  UserPreference: "UserPreference",
  Vote: "Vote",
  Voting: "Voting",
}

/**
 * Default registration trigrams per entity.
 * They must be unique as this is used in AI to compute links to entities.
 * In case they are not unique, if conversations do not mix confusing registrations it could still be parsed correctly.
 *
 * Note: tenants can change trigrams in their tenant configuration or through the field "type" in many entities.
 * To find these additional values, the platform's API that returns the trigrams read through entities' type list and tenant config.
 *
 * `getEntityTrigrams()` returns the full list. Each value is either a fixed trigram string, or a rule
 * object describing how the trigram is derived from a field on the entity instance:
 *   { field, map, default }        -> map[entity[field]] when present, else default (a field value picks a variant)
 *   { field, firstChars, default } -> entity[field].substring(0, firstChars), else default
 *   { field, useIfLen, default }   -> entity[field] when its length === useIfLen, else default
 *   { field }                      -> entity[field] itself (the field IS the trigram)
 *   { default, variants: {…} }     -> a group of fixed trigrams for one entity (e.g. Person): `default`
 *                                     is the entity's own trigram; `variants` holds the named sub-trigrams
 *                                     in their own object so their names can never clash with the keys above
 * Use `getEntityTrigram(entityName, entity)` to resolve one, applying its rule.
 *
 * Left inline at the call site (not modelled here): Budget ("BGT-" + trigram) and
 * PrivateEquityAsset (a type map whose fallback "ASS" is shared with Asset).
 */
const entityTrigrams = {
  AccountingCatalogItem: "AIT",
  Agreement: "AGR",
  AiAgent: "AGI",
  AiMemory: "AIM",
  Amendment: { field: "type" },
  AmendmentCatalogItem: "AMD",
  Asset: "ASS",
  AssetCatalogItem: "ACI",
  AssetExpense: "AEX",
  AssetLot: { field: "type", map: { TRANSPORT: "ALT" }, default: "ALO" },
  AssetServiceCatalogItem: "ASI",
  BankAccount: "ACC",
  BankCard: "BCD",
  BankOperation: "OPE",
  BankStatement: "STA",
  BankTransaction: "TRA",
  Batch: "BAT",
  Booking: "BOK",
  Campaign: "CPG",
  Camt: { field: "method" },
  Case: { field: "type", firstChars: 3, default: "CAS" },
  Claim: "CLM",
  Collateral: "CLT",
  Committee: "CMT",
  Contract: { field: "type" },
  ContractElement: { field: "type" },
  ContractLot: "LOT",
  Covenant: "CVN",
  CreditLine: "CRL",
  CreditLineElement: { field: "type" },
  Deal: "FIN",
  DepreciationSchedule: "DSC",
  Document: "DOC",
  EInvoiceLot: "EIL",
  ExternalData: "EXT",
  FinancingService: "FSV",
  Fine: "FNE",
  Flow: "FLO",
  Fund: { field: "type", useIfLen: 3, default: "FND" },
  Funding: "FNG",
  Import: "IMP",
  Index: "IDX",
  Indice: "IDC",
  Inspection: "SRP",
  // Invoice entity has no trigram :(
  LedgerAccount: "LAC",
  LedgerBalance: "LBL",
  LedgerPeriod: "LPD",
  Location: "LOC",
  Maintenance: "MTN",
  Note: "NOT",
  PaymentFile: "PMF",
  PaymentFileLine: "PFL",
  PersonAmendment: "PAM",
  Person: {
    default: "PER",
    variants: {
      automatic: "AUT",
      siren: "SIR",
      europeanVat: "EVA",
      organization: "ORG",
      individual: "IND",
      partner: "PTN",
      funder: "PFN",
      professional: "PRO",
      company: "COM",
      employee: "EMP",
    },
  },
  Prospect: "PRS",
  Posting: { field: "dc", map: { E: "EVT" }, default: "PST" },
  PostingEvent: "EVT",
  PrivateEquityOperation: "PEO",
  Product: "PRD",
  ProductCatalogItem: "PCI",
  ProductElementCatalogItem: "PEI",
  ProductTable: "PTB",
  Project: "PRJ",
  ProjectEstimate: "PES",
  Publication: "PUB",
  Question: "QST",
  Quotation: "QOT",
  Rule: { field: "type", firstChars: 3, default: "RUL" },
  Scheme: "SCH",
  Score: "SCR",
  ScoreCatalogItem: "SCI",
  Sepa: { field: "method", list: "paymentMode" },
  Sheet: "SHT",
  Task: "TSK",
  Tax: "TAX",
  Ticket: "TCK",
  TicketLot: "TIL",
  TimeEntry: "TIM",
  Vote: "VOT",
  Voting: "VTG",
}

function getEntityTrigrams() {
  return entityTrigrams
}

/**
 * Resolves the registration trigram for an entity, applying any field-based rule.
 * @param {string} entityName
 * @param {object} [entity] The entity instance (required for entities whose trigram derives from a field).
 * @returns {string|undefined}
 */
function getEntityTrigram(entityName, entity) {
  const entityTrigramItem = entityTrigrams[entityName]
  if (entityTrigramItem === undefined || entityTrigramItem === null || typeof entityTrigramItem === "string") {
    return entityTrigramItem
  }
  const value = entity?.[entityTrigramItem.field]
  if (entityTrigramItem.map) {
    return (value !== undefined && value !== null && entityTrigramItem.map[value]) || entityTrigramItem.default
  }
  if (entityTrigramItem.firstChars) {
    return value !== undefined && value !== null ? String(value).substring(0, entityTrigramItem.firstChars) : entityTrigramItem.default
  }
  if (entityTrigramItem.useIfLen) {
    return value?.length === entityTrigramItem.useIfLen ? value : entityTrigramItem.default
  }
  return value || entityTrigramItem.default
}

/**
 * Key is the entity name, value the collection name associated to it, which is the camel case english pluralized form.
 */
const collectionNames = {
  AccessLog: "accessLogs",
  AccessTenantStat: "accessTenantStats", // this one only exists in collection name, there is no entity
  AccessStat: "accessStats",
  AccountingCatalogItem: "accountingCatalogItems",
  Agreement: "agreements",
  AiAgent: "aiAgents",
  AiConversation: "aiConversations",
  AiMemory: "aiMemories",
  AiConversationMessage: "aiConversationMessages",
  Amendment: "amendments",
  AmendmentCatalogItem: "amendmentCatalogItems",
  Asset: "assets",
  AssetCatalogItem: "assetCatalogItems",
  AssetServiceCatalogItem: "assetServiceCatalogItems",
  AssetExpense: "assetExpenses",
  AssetLot: "assetLots",
  Audit: "audits",
  Batch: "batches",
  BankAccount: "bankAccounts",
  BankCard: "bankCards",
  BankOperation: "bankOperations",
  BankStatement: "bankStatements",
  BankTransaction: "bankTransactions",
  BatchResultArchive: "batchResultArchives",
  Booking: "bookings",
  Budget: "budgets",
  Campaign: "campaigns",
  CampaignContact: "campaignContacts",
  Camt: "camts",
  CamtEntry: "camtEntries",
  Cashflow: "cashflows",
  CashflowAllocation: "cashflowAllocations",
  Case: "cases",
  Claim: "claims",
  Collateral: "collaterals",
  Committee: "committees",
  Communication: "communications",
  Contract: "contracts",
  ContractElement: "contractElements",
  ContractLot: "contractLots",
  Counter: "counters",
  Covenant: "covenants",
  CreditLine: "creditLines",
  Currency: "currencies",
  Deal: "deals",
  DepreciationSchedule: "depreciationSchedules",
  Document: "documents",
  Dtxn: "dtxns",
  EInvoiceLot: "eInvoiceLots",
  Evaluation: "evaluations",
  EvaluationCatalogItem: "evaluationCatalogItems",
  ExternalData: "externalDatas",
  FeatureFlag: "featureFlags",
  FeatureFlagItem: "featureFlagItems",
  FinancingService: "financingServices",
  Fine: "fines",
  Flow: "flows",
  Fund: "funds",
  Funding: "fundings",
  History: "histories",
  Import: "imports",
  Index: "indexes",
  Indice: "indices",
  Inspection: "inspections",
  Inventory: "inventories",
  Invoice: "invoices",
  InvoiceLine: "invoiceLines",
  KeyValue: "keyValues",
  LedgerAccount: "ledgerAccounts",
  LedgerPeriod: "ledgerPeriods",
  Lesson: "lessons",
  LessonCatalogItem: "lessonCatalogItems",
  List: "lists",
  Locale: "locales",
  Location: "locations",
  Maintenance: "maintenances",
  Metric: "metrics",
  MetricRecord: "metricRecords",
  Note: "notes",
  PaymentFile: "paymentFiles",
  PaymentFileLine: "paymentFileLines",
  Person: "persons",
  PersonAmendment: "personAmendments",
  Posting: "postings",
  PostingEvent: "postingEvents",
  PrivateEquityAsset: "privateEquityAssets",
  PrivateEquityOperation: "privateEquityOperations",
  PrivateEquityTransaction: "privateEquityTransactions",
  Product: "products",
  ProductCatalogItem: "productCatalogItems",
  ProductElementCatalogItem: "productElementCatalogItems",
  ProductTable: "productTables",
  Project: "projects",
  ProjectEstimate: "projectEstimates",
  Prospect: "prospects",
  Publication: "publications",
  Question: "questions",
  Report: "reports",
  Rule: "rules",
  Scheme: "schemes",
  Score: "scores",
  ScoreCatalogItem: "scoreCatalogItems",
  Script: "scripts",
  ScriptLog: "scriptLogs",
  Sheet: "sheets",
  Sepa: "sepas",
  SystemEvent: "systemEvents",
  SystemLog: "systemLogs",
  Task: "tasks",
  Tax: "taxes",
  TmpCollection: "tmpCollections",
  Tenant: "tenants",
  Ticket: "tickets",
  TicketLot: "ticketLots",
  TimeEntry: "timeEntries",
  User: "users",
  UserPreference: "userPreferences",
  Vote: "votes",
  Voting: "votings",
}

const dynosPrefix = "Dyno"

/**
 * Returns the collection name of an entity name, which is the camel case english plural form.
 * Warning: in general avoid using this function, prefer using directly the num collectionNames.
 * However, for Dynos (Dynamic Objects), there is no choice but to use this function.
 * @param {string} entityName - PascalCase entity name (e.g. `Contract`, `Currency`).
 * @returns {string} Camel-case plural Mongo-style collection name (e.g. `contracts`, `currencies`).
 */
function entityNameToCollectionName(entityName) {
  // irregular: Currency => currencies, TimeEntry => timeEntries
  if (entityName.endsWith("y")) {
    return entityName[0].toLowerCase() + entityName.substring(1, entityName.length - 1) + "ies"
  }

  // irregular: Tax => taxes, Batch => batches
  if (entityName.endsWith("x") || entityName.endsWith("h")) {
    return entityName[0].toLowerCase() + entityName.substring(1) + "es"
  }

  // regular, just append "s": TmpCollection => tmpCollections, SystemEvent => systemEvents
  return entityName[0].toLowerCase() + entityName.substring(1) + "s"
}

/**
 * @returns {Record<string, string>} Map of camelCase entity keys to client-side route paths (e.g. `contract` → `/contract`).
 */
function getEntityClientRoutes() {
  return {
    accessLog: "/access-log",
    accountingCatalogItem: "/accounting-catalog-item",
    agreement: "/agreement",
    aiAgent: "/ai-agent",
    aiConversation: "/ai-conversation",
    aiMemory: "/ai-memory",
    amendment: "/amendment",
    amendmentCatalogItem: "/amendment-catalog-item",
    asset: "/asset",
    assetCatalogItem: "/asset-catalog-item",
    assetServiceCatalogItem: "/asset-service-catalog-item",
    assetLot: "/asset-lot",
    audit: "/audit",
    batch: "/batch",
    bankAccount: "/bank-account",
    bankCard: "/bank-card",
    bankOperation: "/bank-operation",
    bankStatement: "/bank-statement",
    bankTransaction: "/bank-transaction",
    booking: "/booking",
    campaign: "/campaign",
    camt: "/camt",
    camtEntry: "/camt/:camtId/entry",
    cashflow: "/cashflow",
    case: "/case",
    claim: "/claim",
    collateral: "/collateral",
    committee: "/committee",
    communication: "/communication",
    contract: "/contract",
    contractElement: "/contract-element",
    contractLot: "/contract-lot",
    counter: "/counter",
    covenant: "/covenant",
    creditLine: "/credit-line",
    currency: "/currency",
    deal: "/deal",
    depreciationSchedule: "/depreciation-schedule",
    document: "/document",
    dtxn: "/dtxn",
    eInvoiceLot: "/e-invoice-lot",
    evaluation: "/evaluation",
    evaluationCatalogItem: "/evaluation-catalog-item",
    externalData: "/external-data",
    featureFlag: "/feature-flag",
    financingService: "/financing-service",
    fine: "/fine",
    flow: "/flow",
    fund: "/fund",
    funding: "/funding",
    history: "/history",
    inspection: "/inspection",
    inventory: "/inventory",
    invoice: "/invoice",
    invoiceLine: "/invoice",
    index: "/index",
    indice: "/indice",
    keyValue: "/key-value",
    ledgerAccount: "/ledger-account",
    ledgerPeriod: "/ledger-period",
    lesson: "/lesson",
    lessonCatalogItem: "/lesson-catalog-item",
    locale: "/locale",
    maintenance: "/maintenance",
    metric: "/metric",
    note: "/note",
    paymentFile: "/payment-file",
    paymentFileLine: "/payment-file-line",
    person: "/person",
    personAmendment: "/person-amendment",
    posting: "/posting",
    postingEvent: "/posting-event",
    privateEquityAsset: "/private-equity-asset",
    privateEquityOperation: "/private-equity-operation",
    privateEquityTransaction: "/private-equity-transaction",
    product: "/product",
    productCatalogItem: "/product-catalog-item",
    productElementCatalogItem: "/product-element-catalog-item",
    productTable: "/product-table",
    project: "/project",
    projectEstimate: "/project-estimate",
    prospect: "/prospect",
    publication: "/publication",
    question: "/question",
    report: "/report",
    scheme: "/scheme",
    systemEvent: "/system-event",
    systemLog: "/system-log",
    rule: "/rule",
    score: "/score",
    scoreCatalogItem: "/score-catalog-item",
    sepa: "/sepa",
    task: "/task",
    tax: "/tax",
    tenant: "/tenant",
    ticket: "/ticket",
    ticketLot: "/ticket-lot",
    user: "/user",
    scriptLog: "/script-log",
    voting: "/voting",
    vote: "/vote",
  }
}

/**
 * @param {string} entityName - Entity name (PascalCase or camelCase; spaces/hyphens ignored for lookup).
 * @param {boolean} [isNew] - For dynamic (Dyno) entities, append `/new` to the generated route.
 * @returns {string} Client route path for list/detail navigation.
 * @throws {Error} When `entityName` is missing or not supported (unless it is a Dyno entity).
 */
function getEntityClientRoute(entityName, isNew) {
  if (!entityName) throw Error(genericErrors.MISSING_FIELD_IN_ARGS + "'entityName'")
  const clientRoutes = getEntityClientRoutes()
  const lower = entityName.toLowerCase().replace(/( |-)/g, "")
  const key = Object.keys(clientRoutes).find(key => key.toLowerCase().replace(/( |-)/g, "") === lower)
  if (!key) {
    if (entityName.toLowerCase().startsWith("dyno")) {
      let route = "/page/" + toPlural(toDashed(entityName.substring(4)))
      if (isNew) route += "/new"
      return route
    } else {
      throw Error(genericErrors.UNSUPPORTED_ENTITY + entityName)
    }
  }
  return clientRoutes[key]
}

/**
 * @returns {Record<string, string>} Map of camelCase entity keys to REST API URL prefixes (e.g. `contract` → `/api/financing/contracts`).
 */
function getEntityServerRoutes() {
  return {
    accessLog: "/api/system/access-logs",
    accountingCatalogItem: "/api/accounting/accounting-catalog-items",
    agreement: "/api/financing/agreements",
    aiAgent: "/api/ai/agents",
    aiConversation: "/api/ai/conversations",
    aiMemory: "/api/ai/memories",
    amendment: "/api/financing/amendments",
    amendmentCatalogItem: "/api/financing/amendment-catalog-items",
    asset: "/api/asset/assets",
    assetCatalogItem: "/api/asset/asset-catalog-items",
    assetServiceCatalogItem: "/api/asset/asset-service-catalog-items",
    assetLot: "/api/asset/asset-lots",
    audit: "/api/core/audits",
    bankAccount: "/api/bank/bank-accounts",
    bankCard: "/api/bank/bank-cards",
    bankOperation: "/api/bank/bank-operations",
    bankTransaction: "/api/bank/bank-transactions",
    bankStatement: "/api/bank/bank-statements",
    batch: "/api/script/batches",
    booking: "/api/accounting/bookings",
    collateral: "/api/asset/collaterals",
    campaign: "/api/person/campaigns",
    camt: "/api/billing/camts",
    camtEntry: "/api/billing/camt-entries",
    case: "/api/case/cases",
    cashflow: "/api/billing/cashflows",
    claim: "/api/case/claims",
    committee: "/api/support/committees",
    communication: "/api/communication/communications",
    contract: "/api/financing/contracts",
    contractElement: "/api/financing/contract-elements",
    contractLot: "/api/financing/contract-lots",
    counter: "/api/core/counters",
    covenant: "/api/financing/covenants",
    creditLine: "/api/financing/credit-lines",
    currency: "/api/core/currencies",
    deal: "/api/financing/deals",
    depreciationSchedule: "/api/asset/depreciation-schedules",
    document: "/api/core/documents",
    dtxn: "/api/core/dtxns",
    eInvoiceLot: "/api/e-invoicing/e-invoice-lots",
    evaluation: "/api/support/evaluations",
    evaluationCatalogItem: "/api/support/evaluation-catalog-items",
    externalData: "/api/external/external-data",
    featureFlag: "/api/core/feature-flags",
    featureFlagItem: "/api/core/feature-flags",
    financingService: "/api/financing/financing-services",
    fine: "/api/case/fines",
    flow: "/api/core/flows",
    fund: "/api/financing/funds",
    funding: "/api/financing/fundings",
    history: "/api/core/histories",
    index: "/api/core/indexes",
    indice: "/api/core/indices",
    inspection: "/api/asset/inspections",
    inventory: "/api/accounting/inventories",
    invoice: "/api/billing/invoices",
    keyValue: "/api/core/key-values",
    ledgerAccount: "/api/accounting/ledger-accounts",
    ledgerPeriod: "/api/accounting/ledger-periods",
    lesson: "/api/support/lessons",
    lessonCatalogItem: "/api/support/lesson-catalog-items",
    list: "/api/script/lists",
    locale: "/api/core/locales",
    location: "/api/asset/locations",
    maintenance: "/api/asset/maintenances",
    metric: "/api/core/metrics",
    note: "/api/core/notes",
    paymentFile: "/api/billing/payment-files",
    paymentFileLine: "/api/billing/payment-file-lines",
    person: "/api/person/persons",
    personAmendment: "/api/person/person-amendments",
    voting: "/api/support/votings",
    posting: "/api/accounting/postings",
    postingEvent: "/api/accounting/posting-events",
    privateEquityAsset: "/api/private-equity/assets",
    privateEquityOperation: "/api/private-equity/operations",
    privateEquityTransaction: "/api/private-equity/transactions",
    product: "/api/financing/products",
    productCatalogItem: "/api/financing/product-catalog-items",
    productElementCatalogItem: "/api/financing/product-element-catalog-items",
    productTable: "/api/financing/product-tables",
    project: "/api/support/projects",
    projectEstimate: "/api/support/project-estimates",
    budget: "/api/support/budgets",
    prospect: "/api/person/prospects",
    publication: "/api/notification/publications",
    question: "/api/support/questions",
    report: "/api/report/reports",
    rule: "/api/case/rules",
    scheme: "/api/financing/schemes",
    score: "/api/scoring/scores",
    scoreCatalogItem: "/api/scoring/score-catalog-items",
    script: "/api/script/scripts",
    scriptLog: "/api/script/logs",
    sepa: "/api/billing/sepas",
    systemLog: "/api/system/system-logs",
    systemEvent: "/api/system/system-events",
    task: "/api/core/tasks",
    tax: "/api/core/taxes",
    tenant: "/api/core/tenants",
    ticket: "/api/support/tickets",
    ticketLot: "/api/support/ticket-lots",
    timeEntry: "/api/support/time-entries",
    tmpCollection: "/api/core/temporary-collection", // no "s" here...
    user: "/api/core/users",
    userPreference: "/api/core/users",
    vote: "/api/support/votes",
  }
}

/**
 * @param {string} entityName
 * @returns The relative URL of the API managing the given entity. For example providing "contract" would return "/api/financing/contracts".
 */
function getEntityServerRoute(entityName) {
  if (!entityName) throw Error(genericErrors.MISSING_FIELD_IN_ARGS + "'entityName'")
  const serverRoutes = getEntityServerRoutes()
  const lower = entityName.toLowerCase().replace(/ /g, "")

  // ignore pseudo entities
  if (pseudoEntities.includes(lower)) return

  const key = Object.keys(serverRoutes).find(key => key.toLowerCase().replace(/ /g, "") === lower)
  if (!key) {
    if (lower.startsWith("dyno")) {
      return "/api/dyno/" + toDashed(toPlural(entityName.substring(4)))
    } else {
      throw Error(genericErrors.UNSUPPORTED_ENTITY + entityName)
    }
  }
  return serverRoutes[key]
}

/**
 * @param {string} entityName - PascalCase entity name (e.g. `Asset`).
 * @returns {string} Name of the corresponding status enumeration (e.g. `assetStatus`).
 * @throws {Error} When `entityName` is missing.
 */
function getEntityStatusListName(entityName) {
  if (!entityName) throw Error(genericErrors.MISSING_FIELD_IN_ARGS + "'entityName'")
  return `${lowerCaseFirstLetter(entityName)}Status` // "Asset" => "assetStatus"
}

/**
 * Resolves the PascalCase entity name from a client route path.
 *
 * @param {{ clientRoute: string }} params
 * @param {string} params.clientRoute - A route from {@link getEntityClientRoutes} values, or `/page/...` for Dyno entities.
 * @returns {string|undefined} Entity name (e.g. `Contract`, `DynoExternalPerson`), or `undefined` if no match.
 * @throws {Error} When `clientRoute` is missing.
 */
function getEntityName({ clientRoute }) {
  if (!clientRoute) throw Error(genericErrors.MISSING_FIELD_IN_ARGS + "'clientRoute'")

  if (clientRoute.startsWith("/page/")) {
    // /page/external-persons => DynoExternalPerson
    return (
      dynosPrefix +
      capitalizeFirstLetter(
        toSingular(
          clientRoute
            .substring(6)
            .split("-")
            .map(it => capitalizeFirstLetter(it))
            .join(""),
        ),
      )
    )
  }

  clientRoute = toSingular(clientRoute)

  const clientRoutes = getEntityClientRoutes()
  for (const entityName in clientRoutes) if (clientRoutes[entityName] === clientRoute) return capitalizeFirstLetter(entityName)
}

/**
 * @param {string} entityName - PascalCase entity name.
 * @returns {string} Field/list name for document types (e.g. `contractDocumentType`).
 * @throws {Error} When `entityName` is missing.
 */
function getEntityDocumentTypeListName(entityName) {
  if (!entityName) throw Error(genericErrors.MISSING_FIELD_IN_ARGS + "'entityName'")
  return `${lowerCaseFirstLetter(entityName)}DocumentType`
}

/**
 * @param {string} entityName - PascalCase entity name.
 * @returns {string} Field/list name for history types (e.g. `contractHistoryType`).
 * @throws {Error} When `entityName` is missing.
 */
function getEntityHistoryTypeListName(entityName) {
  if (!entityName) throw Error(genericErrors.MISSING_FIELD_IN_ARGS + "'entityName'")
  return `${lowerCaseFirstLetter(entityName)}HistoryType`
}

const batchStatuses = {
  DRAFT: "DRAFT",
  FETCHING: "FETCHING",
  PROCESSING: "PROCESSING",
  STOPPED: "STOPPED",
  ERROR: "ERROR",
  WARNING: "WARNING",
  SUCCESS: "SUCCESS",
}

const batchActions = {
  DO: "DO",
  UNDO: "UNDO",
  // continue is equivalent to retry
  CONTINUE: "CONTINUE",
  COMPLETE: "COMPLETE",
  RESET: "RESET",
  STOP_FETCHING: "STOP_FETCHING",
}

const requestOriginators = {
  basikonClient: "basikon-client",
}

export {
  batchActions,
  batchStatuses,
  collectionNames,
  coreTenants,
  dynosPrefix,
  entityNames,
  entityNameToCollectionName,
  getEntityClientRoute,
  getEntityClientRoutes,
  getEntityDocumentTypeListName,
  getEntityHistoryTypeListName,
  getEntityName,
  getEntityServerRoute,
  getEntityServerRoutes,
  getEntityStatusListName,
  getEntityTrigram,
  getEntityTrigrams,
  pseudoEntities,
  requestOriginators,
}
